import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import "../checkbox/checkbox.element";
import "../icon/icon.element";

@customElement('cds-table-cell')
export class TableCellElement extends LitElement {
  static styles = css`
    :host {
      align-items: center;
      color: var(--token-component-table-row-text-default, light-dark(#343438, #ffffff));
      display: flex;
      flex: 1 0 0;
      font-family: var(--token-global-font-font-family-brand, 'Nunito Sans');
      font-size: var(--token-global-font-font-size-xs, 0.875rem); /* 14px */
      font-style: normal;
      font-weight: var(--token-global-font-font-weight-regular-normal, 400);
      gap: var(--token-global-spacing-s8, 8px);
      line-height: var(--token-global-font-line-height-xs, 1.25rem); /* 20px */
      padding: 10px 12px;
      min-width: 0;
    }

    .cds-table-row-checkbox,
    .cds-table-row-expand-icon {
      display: none;
    }

    .cds-table-row-expand-icon {
      transition: transform 0.2s ease-in-out;
    }

    :host([expandable]) {
      .cds-table-row-expand-icon {
        display: inline-block;
      }
    }

    :host([expanded]) {
      .cds-table-row-expand-icon {
        display: inline-block;
        transform: rotate(90deg);
      }
    }

    :host([selectable]) {
      .cds-table-row-checkbox {
        display: inline-block;
      }
    }

    ::slotted(cds-menu) {
      margin-left: auto;
    }
  `;

  @property({ type: String, reflect: true })
  role = 'cell';

  @property({ type: Boolean, reflect: true })
  expandable = false;

  @property({ type: Boolean, reflect: true })
  expanded = false;

  @property({ type: Boolean, reflect: true })
  selectable = false;

  @property({ type: Boolean, reflect: true })
  selected = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  private observer: MutationObserver | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.setupParentObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  render() {
    return html`
      ${this.selectable
        ? html`
          <cds-checkbox
            class="cds-table-row-checkbox"
            .checked=${this.selected}
            ?disabled=${this.disabled}
            @change=${this.handleSelect}
          ></cds-checkbox>
        `
        : ''}
      ${this.expandable
        ? html`
          <cds-icon
            class="cds-table-row-expand-icon"
            icon="chevron-right"
            @click=${this.handleExpand}
          ></cds-icon>
        `
        : ''}
      <slot></slot>
    `;
  }

  private handleExpand() {
    this.dispatchEvent(
      new CustomEvent('expand', {
        detail: this,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleSelect() {
    this.selected = !this.selected;
    this.dispatchEvent(
      new CustomEvent('select', {
        detail: this,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private setupParentObserver() {
    const parentRow = this.closest('cds-table-row');
    if (!parentRow) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'selected') {
          this.selected = parentRow.hasAttribute('selected');
        }
      });
    });

    this.observer.observe(parentRow, {
      attributes: true,
      attributeFilter: ['selected']
    });

    // Set initial state
    this.selected = parentRow.hasAttribute('selected');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-table-cell': TableCellElement;
  }
}
